import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import clsx from "clsx";

import { PopupModal } from "../../UI/Modal/Modal";
import { importContacts, resetImportContacts } from "../../../actions/addressBookAction";
import { downloadTemplate } from "./csvUtils";
import { Localize } from "../Localize";
import { Localize as AriaLocalize } from "../../../shared/ariaLabelLocalize";
import { DefaultLanguage } from "../../../constants/Constants";

const ACCEPTED_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "text/csv",
  "application/vnd.ms-excel",
];
const MAX_FILE_SIZE_MB = 5;

const ImportContactsModal = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { importContactsLoading } = useSelector((state) => state.addressBook);
  const langId = useSelector((state) => state.language?.selectedlanguage?.id);
  const [localisedValues, setLocalisedValues] = useState(Localize[langId] || Localize[DefaultLanguage]);
  const ariaLocalised = AriaLocalize[langId] || AriaLocalize['en_US'];

  useEffect(() => {
    if (langId) {
      setLocalisedValues(Localize[langId] || Localize[DefaultLanguage]);
    }
  }, [langId]);
  const fileInputRef = useRef(null);

  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);

  const handleClose = useCallback(() => {
    dispatch(resetImportContacts());
    setSelectedFile(null);
    setImportResult(null);
    setError(null);
    setDragActive(false);
    onClose();
  }, [dispatch, onClose]);

  const handleFileSelect = useCallback((file) => {
    setError(null);
    setSelectedFile(null);

    if (!file) return;

    const isValidType =
      ACCEPTED_TYPES.includes(file.type) ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".csv");

    if (!isValidType) {
      setError(localisedValues['invalid_file_type']);
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(localisedValues['file_too_large'].replace('{n}', MAX_FILE_SIZE_MB));
      return;
    }

    setSelectedFile(file);
  }, [localisedValues]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleClearFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setError(null);
    try {
      const result = await dispatch(importContacts(selectedFile));
      if (!result?.failedCount) {
        onSuccess(); // all imported, close modal + toast
      } else {
        // A11Y-10: result view has its own role="alert" (in-modal, aria-modal
        // scopes NVDA to the dialog so a background LiveRegion push is dropped).
        setImportResult(result);
      }
    } catch (err) {
      setError(err.message || localisedValues['import_failed']);
    }
  };

  const handleDone = () => {
    if (importResult?.importedCount > 0) {
      onSuccess(); // refreshes contacts list + closes modal
    } else {
      handleClose();
    }
  };

  const headerContent = (
    <div className="modal-title-box">
      <h2 className="modal-title">{localisedValues['import_contacts']}</h2>
    </div>
  );

  // Results view shown after import
  if (importResult) {
    const hasErrors = importResult.errors && importResult.errors.length > 0;
    const allFailed = importResult.importedCount === 0 && importResult.failedCount > 0;
    const errorsRegionLabel = ariaLocalised.rows_not_imported_region.replace("{n}", importResult.errors?.length ?? 0);

    const bodyContent = (
      <div className="import-result-step">
        <p className="visually-hidden" role="alert">
          {ariaLocalised.import_complete_summary
            .replace("{imported}", importResult.importedCount ?? 0)
            .replace("{failed}", importResult.failedCount ?? 0)
            .replace("{total}", importResult.totalRecords ?? 0)}
        </p>
        <div className="import-result-summary">
          <div className="result-stat">
            <span className="result-stat-value">{importResult.totalRecords ?? 0}</span>
            <span className="result-stat-label">{localisedValues['total']}</span>
          </div>
          <div className="result-stat result-stat--success">
            <span className="result-stat-value">{importResult.importedCount ?? 0}</span>
            <span className="result-stat-label">{localisedValues['imported']}</span>
          </div>
          {importResult.failedCount > 0 && (
            <div className="result-stat result-stat--failed">
              <span className="result-stat-value">{importResult.failedCount}</span>
              <span className="result-stat-label">{localisedValues['failed']}</span>
            </div>
          )}
        </div>

        {hasErrors && (
          <div className="import-result-errors">
            <p className="import-result-errors-title">
              <span className="icon-warning" aria-hidden="true"></span>
              {(importResult.errors.length === 1 ? localisedValues['one_row_not_imported'] : localisedValues['rows_not_imported']).replace('{n}', importResult.errors.length)}
            </p>
            {/* WCAG 2.1.1 (axe scrollable-region-focusable): this list scrolls
                (max-height + overflow-y) and holds no focusable children, so it
                needs an explicit tab stop for keyboard users. The same tab stop
                also satisfies the rule for the enclosing `.modal-body`, which
                became a scroll container when the dialog was made
                `modal-dialog-scrollable`. */}
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div className="import-errors-list" tabIndex={0} role="group" aria-label={errorsRegionLabel}>
              <div className="import-errors-header">
                <span>{localisedValues['row']}</span>
                <span>{localisedValues['name']}</span>
                <span>{localisedValues['reason']}</span>
              </div>
              {importResult.errors.map((err) => (
                <div key={`${err.rowNumber}-${err.fullName}`} className="import-error-row">
                  <span className="import-error-row-num">
                    <span className="import-error-label">{localisedValues['row']} </span>{err.rowNumber}
                  </span>
                  <span className="import-error-name">
                    <span className="import-error-label">{localisedValues['name']}: </span>{err.fullName || "—"}
                  </span>
                  <span className="import-error-msg">
                    <span className="import-error-label">{localisedValues['reason']}: </span>{err.errorMessage}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {allFailed && !hasErrors && (
          <p className="import-all-failed-msg">{localisedValues['none_imported']}</p>
        )}
      </div>
    );

    const footerContent = (
      <div className="button">
        <button
          type="button"
          className="btn btn-primary font-weight-500"
          onClick={handleDone}
          data-testid="doneButton"
        >
          {localisedValues['done']}
        </button>
      </div>
    );

    return (
      <PopupModal
        modalClass="import-contacts-modal"
        dialogClass="modal-dialog-centered modal-dialog-scrollable"
        bodyClass="custom-scroll-div-thin"
        headerContent={headerContent}
        bodyContent={bodyContent}
        footerContent={footerContent}
        closeModal={handleDone}
        backdrop="static"
      />
    );
  }

  // Upload view
  const bodyContent = (
    <div className="import-upload-step">
      <div className="import-instructions">
        <h3 className="instructions-title">{localisedValues['how_to_import']}</h3>
        <ul className="instructions-list">
          <li>{localisedValues['import_step_template']}</li>
          <li>{localisedValues['import_step_required']}</li>
          <li>{localisedValues['import_step_phone']}</li>
          <li>{localisedValues['import_step_optional']}</li>
          <li>{localisedValues['import_step_max_size'].replace("{n}", MAX_FILE_SIZE_MB)}</li>
        </ul>
      </div>

      <div
        className={clsx("upload-dropzone", dragActive && "drag-active")}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="dropzone"
      >
        <button
          type="button"
          className="btn-unstyled upload-dropzone-trigger"
          onClick={handleBrowseClick}
        >
          <span className="dropzone-icon">
            <span className="icon-imgdwn" aria-hidden="true"></span>
          </span>
          <span className="dropzone-text">
            <span className="dropzone-title">{localisedValues['drop_title']}</span>
            <span className="dropzone-subtitle">
              {localisedValues['drop_or']} <span className="browse-link">{localisedValues['browse_files']}</span>
            </span>
          </span>
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.csv"
        onChange={handleFileInputChange}
        style={{ display: "none" }}
        data-testid="fileInput"
      />

      {selectedFile && (
        <div className="selected-file">
          <span className="file-type-icon icon-csv" aria-hidden="true"></span>
          <span className="file-name">{selectedFile.name}</span>
          <span className="file-meta">{(selectedFile.size / 1024).toFixed(1)} {localisedValues['kb']}</span>
          <button type="button" className="btn-remove-file" onClick={handleClearFile} title={ariaLocalised.remove_file} aria-label={ariaLocalised.remove_file}>
            <span className="icon-close" aria-hidden="true"></span>
          </button>
        </div>
      )}

      {error && <div className="validation-error" role="alert">{error}</div>}

      <div className="template-download">
        <p>{localisedValues['need_template']}</p>
        <button
          type="button"
          className="btn btn-link"
          onClick={downloadTemplate}
          data-testid="downloadTemplateButton"
        >
          <span className="icon-download" aria-hidden="true"></span>
          {localisedValues['download_template']}
        </button>
      </div>
    </div>
  );

  const footerContent = (
    <>
      <div className="button">
        <button
          type="button"
          className="btn btn-outline-secondary font-weight-500"
          onClick={handleClose}
          data-testid="cancelButton"
        >
          {localisedValues['cancel']}
        </button>
      </div>
      <div className="button">
        <button
          type="button"
          className="btn btn-primary font-weight-500 import-submit-btn"
          onClick={handleImport}
          disabled={importContactsLoading || !selectedFile}
          data-testid="importButton"
        >
          {importContactsLoading && (
            <span className="for-icon icon-loader spin" aria-hidden="true"></span>
          )}
          {localisedValues['import']}
        </button>
      </div>
    </>
  );

  if (!isOpen) return null;

  return (
    <PopupModal
      modalClass="import-contacts-modal"
      dialogClass="modal-dialog-centered modal-dialog-scrollable"
      bodyClass="custom-scroll-div-thin"
      headerContent={headerContent}
      bodyContent={bodyContent}
      footerContent={footerContent}
      closeModal={handleClose}
      backdrop="static"
      disablekeyboard={importContactsLoading}
    />
  );
};

ImportContactsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ImportContactsModal;
